import React, { useState, useRef, useMemo, useCallback } from 'react';

import {
  UploadOutlined,
  ScissorOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Button,
  Slider,
  Switch,
  Input,
  Modal,
  Typography,
  Divider,
  Tabs,
  Space,
} from 'antd';

import { AdImageSettings, DEFAULT_AD_IMAGE_SETTINGS } from '../types';

const { Text } = Typography;
const { TabPane } = Tabs;

// ============================================================
// CROP MODAL (adapted from uploaded App.tsx CropModal)
// ============================================================

interface CropModalProps {
  visible: boolean;
  imageSrc: string;
  onCropComplete: (croppedDataUrl: string) => void;
  onCancel: () => void;
}

export const CropModal: React.FC<CropModalProps> = ({ visible, imageSrc, onCropComplete, onCancel }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });
  const [cropX, setCropX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);

  const displayWidth = 540;
  const displayHeight = imgDims.width > 0 ? (imgDims.height / imgDims.width) * displayWidth : 400;
  const scale = imgDims.width > 0 ? imgDims.width / displayWidth : 1;

  // Crop area: full height, width = height * 4/3
  const cropWidth = imgDims.height * (4 / 3);
  const maxCropX = Math.max(0, imgDims.width - cropWidth);

  const handleImageLoad = () => {
    const img = new Image();
    img.onload = () => {
      setImgDims({ width: img.width, height: img.height });
      setCropX(Math.max(0, (img.width - img.height * (4 / 3)) / 2));
    };
    img.src = imageSrc;
  };

  React.useEffect(() => {
    if (visible && imageSrc) handleImageLoad();
  }, [visible, imageSrc]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setIsDragging(true);
    setDragStartX(e.clientX - rect.left - cropX / scale);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let newX = (e.clientX - rect.left - dragStartX) * scale;
    newX = Math.max(0, Math.min(newX, maxCropX));
    setCropX(newX);
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleCrop = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = cropWidth;
    canvas.height = imgDims.height;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, cropX, 0, cropWidth, imgDims.height, 0, 0, cropWidth, imgDims.height);
      onCropComplete(canvas.toDataURL('image/jpeg', 0.95));
    };
    img.src = imageSrc;
  };

  return (
    <Modal
      title={<span><ScissorOutlined style={{ marginRight: 8 }} />Обрезка изображения под 4:3</span>}
      visible={visible}
      onCancel={onCancel}
      width={600}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel}>Отмена</Button>
          <Button type="primary" onClick={handleCrop}>Обрезать</Button>
        </div>
      }
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
        Перетащите рамку влево или вправо, чтобы выбрать область для обрезки
      </Text>
      <div
        ref={containerRef}
        style={{ position: 'relative', width: displayWidth, height: displayHeight, cursor: 'move', userSelect: 'none', margin: '0 auto' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img src={imageSrc} alt="Crop" draggable={false} style={{ width: displayWidth, height: displayHeight, display: 'block' }} />
        {/* Darkened areas */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: cropX / scale, height: '100%', background: 'rgba(0,0,0,0.5)' }} />
        <div style={{ position: 'absolute', top: 0, left: (cropX + cropWidth) / scale, right: 0, height: '100%', background: 'rgba(0,0,0,0.5)' }} />
        {/* Crop frame */}
        <div style={{
          position: 'absolute',
          left: cropX / scale,
          top: 0,
          width: cropWidth / scale,
          height: '100%',
          border: '2px solid #1890ff',
          borderStyle: 'dashed',
          boxSizing: 'border-box',
        }} />
      </div>
    </Modal>
  );
};

// ============================================================
// IMAGE PREVIEW (adapted from uploaded App.tsx)
// ============================================================

interface GuidelinesState {
  show1x1: boolean;
  show3x4: boolean;
  show16x9: boolean;
}

interface ImagePreviewInternalProps {
  imageSrc: string;
  settings: AdImageSettings;
  guidelines: GuidelinesState;
}

const ImagePreviewInternal: React.FC<ImagePreviewInternalProps> = ({ imageSrc, settings, guidelines }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageHeight, setImageHeight] = useState(0);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageHeight(e.currentTarget.offsetHeight);
  };

  // Text area width = (height / 4) * 3 * 1.1 (same logic as reference)
  const textWidthPixels = (imageHeight / 4) * 3 * 1.1;
  const containerWidth = containerRef.current?.offsetWidth || 400;

  // Auto font size calculation
  const calculateFontSize = useCallback((text: string, isButton: boolean) => {
    if (!text.trim()) return isButton ? 14 : 20;
    const baseSize = (containerWidth / 15) * 1.1;
    const availableWidth = isButton
      ? textWidthPixels - 80
      : textWidthPixels - (settings.sideMargin * 2);

    let fontSize = isButton ? Math.max(containerWidth / 15, 12) : baseSize;
    const minFontSize = fontSize * 0.3;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return fontSize;

    while (fontSize > minFontSize) {
      ctx.font = `bold ${fontSize}px ${settings.fontFamily}`;
      const words = text.split(' ');
      let lines = 0;
      let currentLine = '';
      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(testLine).width > availableWidth && currentLine) {
          lines++;
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      lines++;
      if (lines <= 2) break;
      fontSize *= 0.95;
    }
    return Math.max(fontSize, minFontSize);
  }, [containerWidth, textWidthPixels, settings.sideMargin, settings.fontFamily]);

  const splitText = useCallback((text: string, fontSize: number, maxWidth: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return [text];
    ctx.font = `bold ${fontSize}px ${settings.fontFamily}`;
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  }, [settings.fontFamily]);

  const mainFontSize = useMemo(() => calculateFontSize(settings.overlayText, false), [calculateFontSize, settings.overlayText]);
  const buttonFontSize = useMemo(() => calculateFontSize(settings.buttonText, true), [calculateFontSize, settings.buttonText]);
  const mainTextMaxWidth = textWidthPixels - (settings.sideMargin * 2);
  const mainTextLines = useMemo(() => splitText(settings.overlayText, mainFontSize, mainTextMaxWidth), [splitText, settings.overlayText, mainFontSize, mainTextMaxWidth]);
  const buttonLines = useMemo(() => splitText(settings.buttonText, buttonFontSize, textWidthPixels - 80).slice(0, 2), [splitText, settings.buttonText, buttonFontSize, textWidthPixels]);

  const textShadowValue = settings.textShadow
    ? `${settings.textShadowX}px ${settings.textShadowY}px ${settings.textShadowBlur}px ${settings.textShadowColor}${Math.round(settings.textShadowOpacity * 2.55).toString(16).padStart(2, '0')}`
    : 'none';

  const buttonShadowValue = settings.buttonShadow
    ? `${settings.buttonShadowX}px ${settings.buttonShadowY}px ${settings.buttonShadowBlur}px ${settings.buttonShadowColor}${Math.round(settings.buttonShadowOpacity * 2.55).toString(16).padStart(2, '0')}`
    : 'none';

  return (
    <div ref={containerRef} className="aie-preview">
      <img src={imageSrc} alt="Ad" className="aie-preview__img" onLoad={handleImageLoad} />

      {/* Solid color overlay */}
      {settings.overlayOpacity > 0 && (
        <div
          className="aie-preview__gradient"
          style={{
            background: `${settings.overlayColor}${Math.round(settings.overlayOpacity * 2.55).toString(16).padStart(2, '0')}`,
          }}
        />
      )}

      {/* Gradient overlay */}
      {(settings.gradientTopOpacity > 0 || settings.gradientBottomOpacity > 0) && imageHeight > 0 && (
        <div
          className="aie-preview__gradient"
          style={{
            background: `linear-gradient(to bottom,
              ${settings.gradientTopColor}${Math.round(settings.gradientTopOpacity * 2.55).toString(16).padStart(2, '0')} 0%,
              transparent 50%,
              ${settings.gradientBottomColor}${Math.round(settings.gradientBottomOpacity * 2.55).toString(16).padStart(2, '0')} 100%)`
          }}
        />
      )}

      {/* Text + Button overlay */}
      <div
        className="aie-preview__overlay"
        style={{
          left: `${settings.sideMargin}px`,
          right: `${settings.sideMargin}px`,
          bottom: `${settings.bottomMargin}%`,
        }}
      >
        <div style={{ width: `${textWidthPixels}px`, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {settings.overlayText && (
            <div style={{
              color: settings.textColor,
              fontFamily: settings.fontFamily,
              textShadow: textShadowValue,
              fontSize: `${mainFontSize}px`,
              textAlign: 'center',
              lineHeight: '0.9',
              fontWeight: 'bold',
            }}>
              {mainTextLines.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          )}

          {settings.buttonText && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                backgroundColor: settings.buttonColor,
                color: settings.buttonTextColor,
                boxShadow: buttonShadowValue,
                fontFamily: settings.fontFamily,
                fontSize: `${buttonFontSize}px`,
                fontWeight: 'normal',
                lineHeight: '1.2',
                padding: '6px 28px',
                borderRadius: `${settings.buttonBorderRadius}px`,
                textAlign: 'center',
              }}>
                {buttonLines.map((line, i) => <div key={i}>{line}</div>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Guidelines 1:1 — vertical lines, spacing = imageHeight */}
      {guidelines.show1x1 && imageHeight > 0 && containerWidth > 0 && (() => {
        const gap = imageHeight;
        const left = (containerWidth - gap) / 2;
        return (
          <div className="aie-preview__guidelines">
            <div className="aie-preview__guideline aie-preview__guideline--blue" style={{ left, top: 0, width: 1, height: '100%' }} />
            <div className="aie-preview__guideline aie-preview__guideline--blue" style={{ left: left + gap, top: 0, width: 1, height: '100%' }} />
          </div>
        );
      })()}

      {/* Guidelines 3:4 — vertical lines, spacing = (height/4)*3 */}
      {guidelines.show3x4 && imageHeight > 0 && containerWidth > 0 && (() => {
        const gap = (imageHeight / 4) * 3;
        const left = (containerWidth - gap) / 2;
        return (
          <div className="aie-preview__guidelines">
            <div className="aie-preview__guideline aie-preview__guideline--red" style={{ left, top: 0, width: 1, height: '100%' }} />
            <div className="aie-preview__guideline aie-preview__guideline--red" style={{ left: left + gap, top: 0, width: 1, height: '100%' }} />
          </div>
        );
      })()}

      {/* Guidelines 16:9 — horizontal lines, spacing = (width/16)*9 */}
      {guidelines.show16x9 && imageHeight > 0 && containerWidth > 0 && (() => {
        const gap = (containerWidth / 16) * 9;
        const top = (imageHeight - gap) / 2;
        return (
          <div className="aie-preview__guidelines">
            <div className="aie-preview__guideline aie-preview__guideline--green" style={{ left: 0, top, width: '100%', height: 1 }} />
            <div className="aie-preview__guideline aie-preview__guideline--green" style={{ left: 0, top: top + gap, width: '100%', height: 1 }} />
          </div>
        );
      })()}
    </div>
  );
};

// ============================================================
// SETTINGS PANELS
// ============================================================

interface SettingsProps {
  settings: AdImageSettings;
  update: <K extends keyof AdImageSettings>(key: K, val: AdImageSettings[K]) => void;
}

const TextSettingsPanel: React.FC<SettingsProps> = ({ settings, update }) => (
  <div className="aie-settings-section">
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Текст на изображении</Text>
      <Input.TextArea rows={2} value={settings.overlayText} onChange={(e) => update('overlayText', e.target.value)} placeholder="Текст заголовка на картинке" />
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Цвет текста</Text>
      <input type="color" value={settings.textColor} onChange={(e) => update('textColor', e.target.value)} />
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Шрифт</Text>
      <Input value={settings.fontFamily} onChange={(e) => update('fontFamily', e.target.value)} size="small" />
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Отступ снизу (%)</Text>
      <Slider min={0} max={40} value={settings.bottomMargin} onChange={(v) => update('bottomMargin', v)} />
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Отступ по бокам (px)</Text>
      <Slider min={0} max={60} value={settings.sideMargin} onChange={(v) => update('sideMargin', v)} />
    </div>
    <Divider style={{ margin: '8px 0' }} />
    <div className="aie-field aie-field--row">
      <Text style={{ fontSize: 12 }}>Тень текста</Text>
      <Switch size="small" checked={settings.textShadow} onChange={(v) => update('textShadow', v)} />
    </div>
    {settings.textShadow && (
      <>
        <div className="aie-field">
          <Text style={{ fontSize: 11 }}>Смещение X / Y</Text>
          <Space>
            <Slider style={{ width: 80 }} min={-10} max={10} value={settings.textShadowX} onChange={(v) => update('textShadowX', v)} />
            <Slider style={{ width: 80 }} min={-10} max={10} value={settings.textShadowY} onChange={(v) => update('textShadowY', v)} />
          </Space>
        </div>
        <div className="aie-field">
          <Text style={{ fontSize: 11 }}>Размытие</Text>
          <Slider min={0} max={20} value={settings.textShadowBlur} onChange={(v) => update('textShadowBlur', v)} />
        </div>
        <div className="aie-field">
          <Text style={{ fontSize: 11 }}>Цвет тени / Прозрачность</Text>
          <Space>
            <input type="color" value={settings.textShadowColor} onChange={(e) => update('textShadowColor', e.target.value)} />
            <Slider style={{ width: 100 }} min={0} max={100} value={settings.textShadowOpacity} onChange={(v) => update('textShadowOpacity', v)} />
          </Space>
        </div>
      </>
    )}
  </div>
);

const ButtonSettingsPanel: React.FC<SettingsProps> = ({ settings, update }) => (
  <div className="aie-settings-section">
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Текст кнопки</Text>
      <Input value={settings.buttonText} onChange={(e) => update('buttonText', e.target.value)} placeholder="Текст кнопки" size="small" />
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Цвет кнопки / Цвет текста</Text>
      <Space>
        <input type="color" value={settings.buttonColor} onChange={(e) => update('buttonColor', e.target.value)} />
        <input type="color" value={settings.buttonTextColor} onChange={(e) => update('buttonTextColor', e.target.value)} />
      </Space>
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Скругление углов</Text>
      <Slider min={0} max={30} value={settings.buttonBorderRadius} onChange={(v) => update('buttonBorderRadius', v)} />
    </div>
    <Divider style={{ margin: '8px 0' }} />
    <div className="aie-field aie-field--row">
      <Text style={{ fontSize: 12 }}>Тень кнопки</Text>
      <Switch size="small" checked={settings.buttonShadow} onChange={(v) => update('buttonShadow', v)} />
    </div>
    {settings.buttonShadow && (
      <>
        <div className="aie-field">
          <Text style={{ fontSize: 11 }}>Смещение X / Y</Text>
          <Space>
            <Slider style={{ width: 80 }} min={-10} max={10} value={settings.buttonShadowX} onChange={(v) => update('buttonShadowX', v)} />
            <Slider style={{ width: 80 }} min={-10} max={10} value={settings.buttonShadowY} onChange={(v) => update('buttonShadowY', v)} />
          </Space>
        </div>
        <div className="aie-field">
          <Text style={{ fontSize: 11 }}>Размытие</Text>
          <Slider min={0} max={20} value={settings.buttonShadowBlur} onChange={(v) => update('buttonShadowBlur', v)} />
        </div>
      </>
    )}
  </div>
);

const BackgroundSettingsPanel: React.FC<SettingsProps> = ({ settings, update }) => (
  <div className="aie-settings-section">
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Заливка цветом</Text>
      <Space>
        <input type="color" value={settings.overlayColor} onChange={(e) => update('overlayColor', e.target.value)} />
        <Slider style={{ width: 140 }} min={0} max={100} value={settings.overlayOpacity} onChange={(v) => update('overlayOpacity', v)} />
        <Text type="secondary" style={{ fontSize: 11, minWidth: 30 }}>{settings.overlayOpacity}%</Text>
      </Space>
    </div>
    <Divider style={{ margin: '8px 0' }} />
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Градиент верх: цвет / прозрачность</Text>
      <Space>
        <input type="color" value={settings.gradientTopColor} onChange={(e) => update('gradientTopColor', e.target.value)} />
        <Slider style={{ width: 120 }} min={0} max={100} value={settings.gradientTopOpacity} onChange={(v) => update('gradientTopOpacity', v)} />
        <Text type="secondary" style={{ fontSize: 11, minWidth: 30 }}>{settings.gradientTopOpacity}%</Text>
      </Space>
    </div>
    <div className="aie-field">
      <Text style={{ fontSize: 12 }}>Градиент низ: цвет / прозрачность</Text>
      <Space>
        <input type="color" value={settings.gradientBottomColor} onChange={(e) => update('gradientBottomColor', e.target.value)} />
        <Slider style={{ width: 120 }} min={0} max={100} value={settings.gradientBottomOpacity} onChange={(v) => update('gradientBottomOpacity', v)} />
        <Text type="secondary" style={{ fontSize: 11, minWidth: 30 }}>{settings.gradientBottomOpacity}%</Text>
      </Space>
    </div>
  </div>
);

// ============================================================
// MAIN EDITOR COMPONENT
// ============================================================

interface AdImageEditorProps {
  visible: boolean;
  imageSrc: string;
  settings: AdImageSettings;
  adTitle: string;
  onClose: () => void;
  onSave: (settings: AdImageSettings) => void;
  onImageChange: (file: File) => void;
}

const AdImageEditor: React.FC<AdImageEditorProps> = ({
  visible,
  imageSrc,
  settings: initialSettings,
  adTitle,
  onClose,
  onSave,
  onImageChange,
}) => {
  const [settings, setSettings] = useState<AdImageSettings>(initialSettings);
  const [guidelines, setGuidelines] = useState<GuidelinesState>({ show1x1: false, show3x4: false, show16x9: false });

  // Reset settings when drawer opens with new data
  React.useEffect(() => {
    if (visible) {
      setSettings({
        ...initialSettings,
        overlayText: initialSettings.overlayText || adTitle,
      });
    }
  }, [visible, initialSettings, adTitle]);

  const update = <K extends keyof AdImageSettings>(key: K, val: AdImageSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  return (
    <Drawer
      title="Редактор изображения"
      visible={visible}
      onClose={onClose}
      width={720}
      bodyStyle={{ padding: 0 }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" onClick={handleSave}>Сохранить</Button>
        </div>
      }
    >
      <div className="aie-layout">
        {/* Preview */}
        <div className="aie-layout__preview">
          <div className="aie-guidelines-btns">
            <Button size="small" type={guidelines.show1x1 ? 'primary' : 'default'} onClick={() => setGuidelines((g) => ({ ...g, show1x1: !g.show1x1 }))}>1:1</Button>
            <Button size="small" type={guidelines.show3x4 ? 'primary' : 'default'} danger={guidelines.show3x4} onClick={() => setGuidelines((g) => ({ ...g, show3x4: !g.show3x4 }))}>3:4</Button>
            <Button size="small" style={guidelines.show16x9 ? { backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' } : {}} onClick={() => setGuidelines((g) => ({ ...g, show16x9: !g.show16x9 }))}>16:9</Button>
          </div>
          <ImagePreviewInternal imageSrc={imageSrc} settings={settings} guidelines={guidelines} />
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            <label>
              <Button icon={<UploadOutlined />} size="small">Заменить изображение</Button>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onImageChange(f); }} />
            </label>
          </div>
        </div>

        {/* Settings */}
        <div className="aie-layout__settings">
          <Tabs defaultActiveKey="text" size="small">
            <TabPane tab="Текст" key="text">
              <TextSettingsPanel settings={settings} update={update} />
            </TabPane>
            <TabPane tab="Кнопка" key="button">
              <ButtonSettingsPanel settings={settings} update={update} />
            </TabPane>
            <TabPane tab="Фон" key="background">
              <BackgroundSettingsPanel settings={settings} update={update} />
            </TabPane>
          </Tabs>
        </div>
      </div>
    </Drawer>
  );
};

export default AdImageEditor;
